<script setup lang="ts">
import { PropType, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import { ICharacterCdn, skillsList } from '@/store/ClassesJobs'
const router = useRouter()
const { mdAndUp, mobile } = useDisplay()

const props = defineProps({
  characterData: {
    type: Object as PropType<ICharacterCdn>,
  },
  selectedOption: {
    type: Number,
    required: true,
  },
  selectedSkill: {
    type: Number,
    required: true,
  },
})
const emits = defineEmits(['change', 'change:skill'])

const showJAT = ref(false)

const learnMore6thJob = () => {
  router.push({
    name: 'sixth-job-skills',
  })
}
const openJobAdvancementTree = () => {
  showJAT.value = true
}

const isZero = computed<boolean>(() => props.characterData?.name.toLowerCase() === 'zero')
const isBeastTamer = computed<boolean>(() => props.characterData?.name.toLowerCase().replace(/ /g, '') === 'beasttamer')

const selectOption = (option: number) => emits('change', option)
const selectSkill = (index: number) => emits('change:skill', index)
</script>
<template>
  <div class="skillsAbilities">
    <div class="skill-selector">
      <div class="selector" :class="{ zero: isZero, beasttamer: isBeastTamer }">
        <template v-for="(skill, _index) in skillsList" :key="_index">
          <div
            :class="{ active: props.selectedOption === _index }"
            @click="selectOption(_index)"
            v-ga-event="{ sectionContent: skill.displayName }"
          >
            {{
              _index === 0
                ? isZero
                  ? 'Alpha/Beta Skills'
                  : isBeastTamer
                  ? 'Animal Skills'
                  : skill.displayName
                : skill.displayName
            }}
          </div>
        </template>
      </div>
      <div class="message">
        These skills can be used starting at Lv.
        {{ isZero && props.selectedOption === 0 ? '100' : skillsList[props.selectedOption].level }}.
      </div>
    </div>
    <div>
      <MSButton v-if="props.selectedOption === 2" class="learnMore6thJobSkills" @click="learnMore6thJob"
        >Learn More</MSButton
      >
      <div v-if="props.selectedOption === 2 && characterData.specialText" class="special-text">
        {{ characterData.specialText }}
      </div>
      <template v-for="(skill, _index) in skillsList" :key="_index">
        <div
          class="skills-wrapper"
          v-if="props.characterData && props.characterData[skill.fieldName] && props.selectedOption === _index"
          :class="{ extend: characterData[skill.fieldName].length > 6 }"
        >
          <div class="skills">
            <div
              v-for="(skill, _index2) in characterData[skill.fieldName]"
              :key="_index2"
              class="skill"
              @click="selectSkill(_index2)"
              :class="{ selected: props.selectedSkill === _index2 }"
              v-ga-hover="{
                interaction: 'content_view',
                sectionContent: skill.title,
              }"
            >
              <v-tooltip v-if="mdAndUp" activator="parent" location="bottom" content-class="cjd-tooltip">
                <div class="tooltip-title">{{ skill.title }}</div>
                <div class="tooltip-content">{{ skill.description }}</div>
              </v-tooltip>
              <img :alt="skill.name" :src="skill.image" />
            </div>
          </div>
        </div>
      </template>
    </div>
    <slot name="skill" />
    <div
      class="job-tree-btn"
      @click="openJobAdvancementTree"
      v-ga-event="{ interaction: 'link_click', sectionContent: 'Job Advancement Tree' }"
    >
      Job Advancement Tree
    </div>
    <JobAdvancementTree :character-data="characterData" v-model="showJAT" />
  </div>
</template>
<style lang="scss" scoped>
.skillsAbilities {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  > div:nth-child(2) {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .skill-selector {
    display: flex;
    flex-direction: column;
    width: pxToRem(445.575px);
    margin-bottom: pxToRem(12.5px);
    > div {
      height: pxToRem(38.3px);
      display: flex;
      align-items: center;
    }
  }
  .selector {
    font-size: pxToRem(15.125px);
    font-family: Glegoo;
    font-weight: bold;
    color: #fff;
    line-height: pxToRem(27.225px);
    margin: pxToRem(10.075px) 0;
    justify-content: space-between;
    &.zero > div:first-child {
      padding: 0 pxToRem(36.25px);
    }
    &.beasttamer > div:first-child {
      padding: 0 pxToRem(50px);
    }
    > div {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      padding: 0 pxToRem(8.125px);
      border-radius: pxToRem(6.25px);
      background-color: #bdbdbd;
      transition: background-color 0.2s linear;
      &.active,
      &:hover {
        background-color: #e46f10;
      }
    }
  }
  .message {
    font-size: pxToRem(16.125px);
    color: #5a5753;
    justify-content: center;
    background-color: #fff;
    border-radius: pxToRem(6.25px);
    position: relative;
    z-index: 1;
    &:after {
      content: '';
      border-top: pxToRem(18.75px) solid #fff;
      border-left: pxToRem(18.75px) solid transparent;
      border-right: pxToRem(18.75px) solid transparent;
      border-bottom: pxToRem(18.75px) solid transparent;
      position: absolute;
      bottom: pxToRem(-28.75px);
    }
  }
  .special-text {
    font-size: pxToRem(16.125);
    width: pxToRem(445.575);
    height: pxToRem(125);
    background-color: #fff;
    border-radius: pxToRem(6.25);
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #5a5753;
    padding: 0 pxToRem(30);
    margin-bottom: pxToRem(10);
    line-height: 1.2;
  }
  .skills-wrapper {
    max-width: pxToRem(252.025px);
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    &.extend {
      max-width: pxToRem(300px);
    }
  }
  .skills {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    z-index: 1;
    max-height: pxToRem(150px);
    .skill {
      width: pxToRem(62.5px);
      height: pxToRem(62.5px);
      margin: pxToRem(6.25px);
      font-size: pxToRem(16.125px);
    }
    img {
      height: 100%;
      width: 100%;
    }
  }
  .learnMore6thJobSkills {
    min-height: pxToRem(32.25px);
    max-height: pxToRem(32.25px);
    font-size: pxToRem(18.15px);
    z-index: 1;
    margin: pxToRem(6.25px) auto;
    width: pxToRem(161.3px);
  }
  .job-tree-btn {
    color: #5a5753;
    font-family: Glegoo;
    font-size: pxToRem(18.15px);
    line-height: pxToRem(32.25px);
    font-weight: bold;
    text-decoration: underline;
    cursor: pointer;
  }
}
:deep(.cjd-tooltip) {
  color: #5a5753 !important;
  background-color: #fff !important;
  font-size: pxToRem(16.125px) !important;
  max-width: pxToRem(297.375px) !important;
  .tooltip-title {
    font-weight: bold !important;
  }
}

@media #{$tablet} {
  div.skillsAbilities {
    padding: 6.5% 0;
    > div:nth-child(2) {
      height: pxToRemTablet(209px);
    }
    .job-tree-btn {
      line-height: 2.5;
      font-size: pxToRemTablet(18.012px);
      color: #fff;
    }
    .skills {
      height: 100%;
      .skill {
        transition: all 0.2s linear;
        cursor: pointer;
        width: pxToRemTablet(100.016px);
        height: pxToRemTablet(100.016px);
        border-radius: pxToRemTablet(5.7px);
        border: pxToRemTablet(5.016px) solid transparent;
        margin: 0;
        &.selected {
          border: pxToRemTablet(5.016px) solid #e46f10;
        }
      }
    }
    .skill-selector {
      width: pxToRemTablet(442.016px);
      margin-bottom: pxToRemTablet(9.5px);
      > div {
        height: pxToRemTablet(38px);
      }
    }
    .learnMore6thJobSkills {
      min-height: pxToRemTablet(30.4px);
      max-height: pxToRemTablet(30.4px);
      font-size: pxToRemTablet(19px);
      width: pxToRemTablet(171px);
    }
    .selector {
      margin: 0 0 pxToRemTablet(9.5px);
      font-size: pxToRemTablet(15.01px);
      line-height: 1;
      &.zero > div:first-child {
        padding: 0 pxToRemTablet(35.72px);
      }
      &.beasttamer > div:first-child {
        padding: 0 pxToRemTablet(49.4px);
      }
      > div {
        padding: 0 pxToRemTablet(7.6px);
        border-radius: pxToRemTablet(3.8px);
      }
    }
    .message {
      font-size: pxToRemTablet(15.998px);
      border-radius: pxToRemTablet(3.8px);
      &:after {
        border-top: pxToRemTablet(13.3px) solid #fff;
        border-left: pxToRemTablet(13.3px) solid transparent;
        border-right: pxToRemTablet(13.3px) solid transparent;
        border-bottom: pxToRemTablet(13.3px) solid transparent;
        bottom: pxToRemTablet(-22.8px);
      }
    }
    .special-text {
      font-size: pxToRemTablet(15.998);
      border-radius: pxToRemTablet(3.8);
      width: pxToRemTablet(442.016);
      height: pxToRemTablet(100);
      padding: pxToRemTablet(20) pxToRemTablet(20);
      margin-bottom: pxToRemTablet(30);
    }
  }
}

@media #{$mobile} {
  div.skillsAbilities {
    padding: 0;
    > div:nth-child(2) {
      height: auto;
      padding: pxToRemMobile(18px) 0;
    }
    .skill-selector {
      width: pxToRemMobile(446.004px);
      margin-bottom: pxToRemMobile(13.5px);
      > div {
        height: pxToRemMobile(37.944px);
      }
    }
    .selector {
      font-size: pxToRemMobile(14.994px);
      margin-bottom: pxToRemMobile(9px);
      &.zero > div:first-child {
        padding: 0 pxToRemMobile(36px);
      }
      &.beasttamer > div:first-child {
        padding: 0 pxToRemMobile(50.4px);
      }
      > div {
        padding: 0 pxToRemMobile(8.55px);
        border-radius: pxToRemMobile(4.5px);
      }
    }
    .message {
      font-size: pxToRemMobile(16.002px);
      border-radius: pxToRemMobile(4.5px);
      &:after {
        border-top: pxToRemMobile(13.5px) solid #fff;
        border-left: pxToRemMobile(13.5px) solid transparent;
        border-right: pxToRemMobile(13.5px) solid transparent;
        border-bottom: pxToRemMobile(13.5px) solid transparent;
        bottom: pxToRemMobile(-22.5px);
      }
    }
    .special-text {
      font-size: pxToRemMobile(16.002);
      border-radius: pxToRemMobile(4.5);
      width: pxToRemMobile(446.004);
      height: auto;
      margin-bottom: 0;
      margin-top: pxToRemMobile(15);
      padding: pxToRemMobile(20) pxToRemMobile(20);
    }
    .skills-wrapper {
      max-width: pxToRemMobile(324px);
      &.extend {
        max-width: pxToRemMobile(432px);
      }
    }
    .skills {
      max-height: unset;
      .skill {
        width: pxToRemMobile(100.008px);
        height: pxToRemMobile(100.008px);
        border: pxToRemMobile(5.004px) solid transparent;
        &.selected {
          border: pxToRemMobile(5.004px) solid #e46f10;
        }
      }
    }
    .job-tree-btn {
      font-size: pxToRemMobile(18px);
      margin: pxToRemMobile(9px) 0;
    }
    .learnMore6thJobSkills {
      min-height: pxToRemMobile(31.5px);
      max-height: pxToRemMobile(31.5px);
      font-size: pxToRemMobile(18px);
      width: pxToRemMobile(162px);
      margin-bottom: pxToRemMobile(18px);
      margin-top: pxToRemMobile(-9px);
    }
  }
}
</style>
